import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMystudioMyPageV4 } from "../../Common/api/v4";
import { MobileBottomTabBar } from "../../components/MobileAppShell";
import { withMobileView } from "../../components/MobileAppShell/mobileNavigation";
import MystudioDataState from "../../components/MystudioDataState";
import CSS from "./static/css/MyInfo.module.css";

type ResponsiveDevice = "mobile" | "tablet";

const getResponsiveDevice = (): ResponsiveDevice => {
    if (typeof window === "undefined") return "mobile";
    return window.matchMedia("(min-width: 700px)").matches ? "tablet" : "mobile";
};

const MyInfo = () => {
    const navigate = useNavigate();
    const myPageQuery = useMystudioMyPageV4();
    const [responsiveDevice, setResponsiveDevice] = useState<ResponsiveDevice>(getResponsiveDevice);
    const profile = myPageQuery.data?.data || null;
    const isMobileView = responsiveDevice === "mobile";

    useEffect(() => {
        const applyResponsiveDevice = () => setResponsiveDevice(getResponsiveDevice());
        applyResponsiveDevice();
        window.addEventListener("resize", applyResponsiveDevice);
        return () => window.removeEventListener("resize", applyResponsiveDevice);
    }, []);

    const handleBack = () => navigate(withMobileView("/tabs/mypage"));
    const handleTabNavigate = (target: string) => navigate(withMobileView(target));

    return (
        <section className={`${CSS.l_MyInfo_tabletPage} ${isMobileView ? CSS.l_MyInfo_mobilePage : ""} mobile-app-frame`}>
            <header className={CSS.l_MyInfo_tabletHeader}>
                <button type="button" onClick={handleBack} aria-label="마이페이지로 돌아가기">
                    <span aria-hidden="true">‹</span>
                </button>
                <h1>회원정보 수정</h1>
                <span aria-hidden="true" />
            </header>

            <main className={CSS.l_MyInfo_tabletScroll}>
                {!myPageQuery.configured ? (
                    <MystudioDataState
                        type="error"
                        title="회원정보를 불러올 수 없어요"
                        description="MyStudio API 연결 설정을 확인해 주세요."
                    />
                ) : myPageQuery.isLoading ? (
                    <MystudioDataState type="loading" title="회원정보를 불러오고 있어요" />
                ) : myPageQuery.isError ? (
                    <MystudioDataState
                        type="error"
                        error={myPageQuery.error}
                        onRetry={() => myPageQuery.refetch()}
                    />
                ) : profile ? (
                    <>
                        <section className={CSS.l_MyInfo_tabletHero}>
                            <span className={CSS.l_MyInfo_tabletHeroIcon} aria-hidden="true">ID</span>
                            <div>
                                <p>회원 기본정보</p>
                                <strong>{profile.displayName || "고객"} 고객님</strong>
                                <small>스튜디오 ERP에 등록된 고객 정보를 확인할 수 있어요.</small>
                            </div>
                        </section>

                        <section className={CSS.l_MyInfo_tabletCard}>
                            <h2>기본 정보</h2>
                            <div className={CSS.l_MyInfo_tabletInfoGrid}>
                                <InfoField label="이름" value={profile.displayName || "정보 없음"} />
                                <InfoField label="전화번호" value={profile.phone || "정보 없음"} />
                                <InfoField label="아이디" value={profile.memberId || "정보 없음"} />
                                <div className={CSS.l_MyInfo_tabletPasswordRow}>
                                    <div>
                                        <span>비밀번호</span>
                                        <strong>비밀번호 변경 기능은 현재 준비 중입니다. 변경이 필요하면 스튜디오에 문의해 주세요.</strong>
                                    </div>
                                    <button
                                        type="button"
                                        disabled
                                        aria-describedby="password-change-unavailable"
                                    >
                                        비밀번호 변경 준비 중
                                    </button>
                                </div>
                                <p id="password-change-unavailable" className={CSS.l_MyInfo_tabletError}>
                                    실제 변경 API가 연결되기 전에는 비밀번호를 변경 완료로 처리하지 않습니다.
                                </p>
                            </div>
                        </section>
                    </>
                ) : (
                    <MystudioDataState
                        type="empty"
                        title="등록된 회원정보가 없어요"
                        description="회원정보가 등록되면 이곳에서 확인할 수 있어요."
                    />
                )}
            </main>

            <MobileBottomTabBar activePath="/tabs/mypage" onNavigate={handleTabNavigate} />
        </section>
    );
};

function InfoField({ label, value }: { label: string; value: string }) {
    return (
        <div className={CSS.l_MyInfo_tabletInfoField}>
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}

export default MyInfo;
